import { useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { useExperienceStore } from '../store/experienceStore'

/**
 * Soft light streaks when time is actively flowing.
 * Kept subtle so they never read as solid slabs.
 */
export function TimeStreaks() {
  const group = useRef<THREE.Group>(null)
  const mats = useRef<THREE.MeshBasicMaterial[]>([])

  const geos = useMemo(() => {
    const list: THREE.BufferGeometry[] = []
    for (let i = 0; i < 12; i++) {
      const pts: THREE.Vector3[] = []
      const side = i % 2 === 0 ? 1 : -1
      const y0 = -1.5 + (i / 12) * 6
      const z0 = -8 - (i % 4) * 1.5
      for (let k = 0; k < 16; k++) {
        const t = k / 15
        pts.push(
          new THREE.Vector3(
            side * (4 + t * 22 + Math.sin(i * 1.3 + t * 5) * 0.8),
            y0 + Math.sin(t * Math.PI + i) * 0.5,
            z0 - t * 4,
          ),
        )
      }
      const curve = new THREE.CatmullRomCurve3(pts)
      // very thin tubes
      list.push(new THREE.TubeGeometry(curve, 48, 0.008 + (i % 3) * 0.004, 4, false))
    }
    return list
  }, [])

  useFrame((state) => {
    const { timeSpeed, traverseIntensity } = useExperienceStore.getState()
    const speed = Math.abs(timeSpeed)
    // only visible while actually moving time
    const show = Math.min(1, Math.max(0, speed * 5 + traverseIntensity * 0.5 - 0.08))
    if (group.current) {
      group.current.visible = show > 0.02
      group.current.rotation.y =
        state.clock.elapsedTime * (0.08 + speed * 0.5) * Math.sign(timeSpeed || 1)
    }
    for (let i = 0; i < mats.current.length; i++) {
      const m = mats.current[i]
      if (!m) continue
      m.opacity = show * (0.08 + (i % 4) * 0.03)
    }
  })

  return (
    <group ref={group} position={[0, 0.5, -4]} renderOrder={2}>
      {geos.map((geo, i) => (
        <mesh key={i} geometry={geo} frustumCulled={false}>
          <meshBasicMaterial
            ref={(el) => {
              if (el) mats.current[i] = el
            }}
            color={i % 3 === 0 ? '#a8ecff' : i % 3 === 1 ? '#ffc98a' : '#e0b8ff'}
            transparent
            opacity={0}
            depthWrite={false}
            depthTest
            blending={THREE.AdditiveBlending}
          />
        </mesh>
      ))}
    </group>
  )
}
